import React from 'react'
import { PullRefresh, List, Loading } from 'react-vant'
import { useTranslation } from 'react-i18next'
import type { RefreshListProps } from './index.type'

export default function RefreshList<T = any>({
  data,
  renderItem,
  keyExtractor,
  loading = false,
  hasMore = true,
  onRefresh,
  onLoadMore,
  emptyText,
  emptyComponent,
  finishedText,
  loadingText,
  className = '',
  itemClassName = '',
  pullRefreshProps,
  listProps
}: RefreshListProps<T>) {
  const { t } = useTranslation()

  // 使用翻译的默认值
  const defaultEmptyText = emptyText || t('暂无数据')
  const defaultFinishedText = finishedText || t('没有更多数据了')
  const defaultLoadingText = loadingText || t('加载中')

  // 空状态渲染
  const renderEmpty = () => {
    if (emptyComponent) return emptyComponent

    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="text-gray-400 text-sm">{defaultEmptyText}</div>
      </div>
    )
  }

  // 包装 onLoadMore 以匹配 List 组件的类型要求
  const handleLoad = async () => {
    if (onLoadMore) {
      await onLoadMore()
    }
  }

  // 判断是否有数据
  const hasData = data.length > 0

  // 首次加载：loading=false 且 data=[] 时，自动触发加载
  React.useEffect(() => {
    if (!loading && !hasData) {
      onRefresh()
    }
  }, [])

  return (
    <div className={className}>
      <PullRefresh
        className="h-full"
        headHeight={80}
        onRefresh={onRefresh}
        {...pullRefreshProps}
        pullingText={t('下拉即可刷新')}
        loosingText={t('释放即可刷新')}
        loadingText={t('加载中')}
      >
        {loading && !hasData ? (
          // 首次加载中且无数据
          <div className="flex items-center justify-center py-20">
            <Loading />
            <span className="ml-2 text-gray-400 text-sm">{defaultLoadingText}</span>
          </div>
        ) : !hasData ? (
          // 空数据状态：显示空状态组件
          <>
            {renderEmpty()}
          </>
        ) : (
          // 有数据：显示列表
          <List
            finished={!hasMore}
            onLoad={handleLoad}
            finishedText={<div className='text-gray-400 text-sm py-6'>{defaultFinishedText}</div>}
            loadingText={<div className='text-gray-400 text-sm py-6'>{defaultLoadingText}</div>}
            {...listProps}
          >
            <div className={itemClassName}>
              {data.map((item, index) => (
                <React.Fragment key={keyExtractor?.(item, index) || index}>
                  {renderItem(item, index)}
                </React.Fragment>
              ))}
            </div>
          </List>
        )}
      </PullRefresh>
    </div>
  )
}
